import type { IAnalyticalBackend } from '@gooddata/sdk-backend-spi';
import { toAfmExecution } from '@gooddata/sdk-backend-tiger';
import { idRef, newAttribute, newMeasure } from '@gooddata/sdk-model';
import { DateInstances } from '@mews-framework/good-data/generated/internal';
import { createUUID } from '@mews/std';
import type { DateRange, Filter, FilterFieldsValues } from '../../../common/types';
import { createMeasureFilters, createReportGDFilters } from '../../filters/utils';
import { createExecution } from '../../lib/createExecution';
import type { RawDataExportConfig } from '../../types';

type ExportAfmQuery = ReturnType<typeof toAfmExecution> & {
    settings: Record<string, unknown>;
};

interface GetExportExecutionProps extends RawDataExportConfig {
    backend: IAnalyticalBackend;
    workspace: string;
    dateRange?: DateRange;
    dateType?: DateInstances;
    filters?: Filter[];
    filterValues?: FilterFieldsValues;
}

export const getExportExecution = ({
    backend,
    workspace,
    attributes,
    dateRange,
    dateType,
    facts,
    filters,
    filterValues,
    metrics,
}: GetExportExecutionProps) => {
    const slicesBy = (attributes || []).map((item) => newAttribute(item.id));

    const gdFilters = createReportGDFilters({
        dateRange,
        dateType,
        filters,
        filterValues,
    });

    const measuresByFacts = (facts || []).map((fact) =>
        newMeasure(idRef(fact.id, 'fact'), (m) =>
            m
                .filters(...createMeasureFilters(fact.filters ?? []))
                .aggregation(fact.aggregation)
                .localId(fact.localId || `m_${fact.id}-${createUUID()}`)
        )
    );
    const measuresByMetrics = (metrics || []).map((metric) =>
        newMeasure(idRef(metric.id, 'measure'), (m) =>
            m
                .filters(...createMeasureFilters(metric.filters ?? []))
                .localId(metric.localId || `m_${metric.id}-${createUUID()}`)
        )
    );

    const executionOptions = {
        backend,
        workspace,
        filters: gdFilters,
        slicesBy,
        seriesBy: [...measuresByFacts, ...measuresByMetrics],
    };

    const execution = createExecution(executionOptions);
    const afm = toAfmExecution(execution.definition) as unknown as ExportAfmQuery;
    afm.settings.mergeHeaders = false;
    return afm;
};
